import { useState, useMemo } from "react";
import CropperComponent from "react-cropper";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import "cropperjs/dist/cropper.css";
import { Buffer } from "buffer";
import Cropper from "cropperjs";
// Material
import { Button, Grid, Backdrop, CircularProgress } from "@mui/material";
import Slider from "@mui/material/Slider";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import UploadProfilePicture from "./UploadProfilePicture";
// API
import { getUserPresignedUrl } from "@/User/api/UserProfile/queries";
import { updateUserProfilePicture } from "@/Common/api/users";
// Helpers
import { urlToBase64 } from "@/User/helpers/urlToBase64";
import { apiInstance } from "@/services/httpAdapter";
import { TGetPresignUrl, TImageCropperComponentProps, IQueryClient, TDragMode, TViewMode } from "../../types/UserProfile";
import { RootState } from "@/redux/store";
import { errorLogger } from "@/Common/helpers/logger";
import { getTenantParameters } from "@Common/helpers/Tenant";

const STATUS_200 = 200;
const TIMEOUT = 800;

export const DEFAULT_PROPS = {
	folderName: "profile-pictures",
	width: 750,
	height: 300,
	containerHeight: 0,
	containerWidth: 0,
	canvasHeight: 0,
	canvasWidth: 0,
	cropBoxHeight: 0,
	cropBoxWidth: 0,
	zoomOnWheel: false,
	viewMode: 0, // 0 ,1 ,2 ,3
	background: true,
	initialAspectRatio: 1,
	aspectRatio: 1,
	cropBoxMovable: false,
	cropBoxResizable: false,
	dragMode: "move", // crop, move ,none
	autoCropArea: 1,
	checkOrientation: false,
	guides: true,
	checkCrossOrigin: false,
	shape: "cropper-cirlce", // cropper-cirlce | cropper-square
	cacheIndex: "imageProfileDataConversion",
};

export default function ImageCropperComponent({
	cacheIndex,
	onCloseCallBack,
	srcImage,
	onClose,
	setUpdatingCallBack,
}: Readonly<TImageCropperComponentProps>) {
	const location = useSelector((state: RootState) => state.location.location);
	const user = useSelector((state: RootState) => state.user.user);

	const [cropper, setCropper] = useState<Cropper>();
	const [originalImage, setOriginalImage] = useState<undefined | null | string>(null);
	const [loading, setLoading] = useState<boolean>(false);

	const queryClient = useQueryClient();

	const uploadFileToS3 = async (file: string) => {
		try {
			const options = {
				headers: {
					"Content-Type": "image/*",
				},
			};

			if (!location) {
				setLoading(false);
				return false;
			}

			const tenantId = getTenantParameters(location.locationId)?.tenant;

			if (!tenantId) {
				setLoading(false);
				return false;
			}

			const response: TGetPresignUrl = await getPresignedUrl(location?.locationId);

			if (!response?.presignedUrl) {
				setLoading(false);
				return false;
			}

			// eslint-disable-next-line no-undef
			const buf = Buffer.from(file.replace(/^data:image\/\w+;base64,/, ""), "base64");
			const uploadResponse = await apiInstance.put(response.presignedUrl, buf, options);
			if (uploadResponse.status === STATUS_200) {
				await updateUserProfilePicture(location?.locationId, user?._id);

				queryClient.setQueryData([cacheIndex], (currentCache: IQueryClient | undefined) => {
					return {
						imageSource: file,
						zoom: currentCache?.zoom,
						rotation: 0,
					};
				});
			}
			return true;
		} catch (error) {
			setLoading(false);
			errorLogger(error as Error | string);
			return false;
		}
	};

	const getPresignedUrl = (locationId: string | undefined): Promise<TGetPresignUrl> => {
		if (locationId) {
			return getUserPresignedUrl(locationId);
		}
		return Promise.resolve(null);
	};

	const updateZoomAmount = (value: number) => {
		queryClient.setQueryData([cacheIndex], (currentCache: IQueryClient | undefined) => {
			return {
				imageSource: currentCache?.imageSource,
				zoom: value,
				rotation: currentCache?.rotation,
			};
		});
	};

	const handleRotation = (value: number) => {
		if (typeof cropper !== "undefined") {
			queryClient.setQueryData([cacheIndex], (currentCache: IQueryClient | undefined) => {
				const totalRotationValue = currentCache?.rotation ? currentCache?.rotation + value : 0;
				if (cropper instanceof Cropper) {
					cropper.rotateTo(totalRotationValue);
				}
				return {
					imageSource: currentCache?.imageSource,
					zoom: currentCache?.zoom,
					rotation: totalRotationValue,
				};
			});
		}
	};

	const handleSaveImage = async () => {
		try {
			setLoading(true);

			if (cropper?.getCroppedCanvas().toDataURL()?.includes("data:image/")) {
				const imageBase64 = cropper.getCroppedCanvas().toDataURL();
				await uploadFileToS3(imageBase64);
				onClose();
				setLoading(false);
				setUpdatingCallBack(true);
				setTimeout(() => setUpdatingCallBack(false), TIMEOUT);
				setOriginalImage(imageBase64);
			}
		} catch (error) {
			errorLogger(error as Error | string);
			setLoading(false);
		}
	};

	const handleChangeImage = (newImage: string) => {
		queryClient.setQueryData([cacheIndex], () => {
			return {
				imageSource: newImage,
				zoom: 0.5,
				rotation: 0,
			};
		});
	};

	const { data: image } = useQuery({
		queryKey: [cacheIndex],
		queryFn: async () => {
			let imageBase64 = null;

			if (srcImage?.includes("https://")) {
				const img = await urlToBase64(srcImage);
				imageBase64 = `data:image/*;base64,${img}`;
				setOriginalImage(imageBase64);
			} else {
				setOriginalImage(srcImage);
				imageBase64 = srcImage;
			}

			return {
				imageSource: imageBase64,
				zoom: 0.5,
				rotation: 0,
			};
		},
		enabled: !!srcImage,
	});

	useMemo(() => {
		const currentCache = queryClient.getQueryData<IQueryClient | undefined>([cacheIndex]);
		if (!!originalImage && onCloseCallBack) {
			setTimeout(() => {
				queryClient.setQueryData([cacheIndex], (currentCacheLocal: IQueryClient | undefined) => {
					return {
						imageSource: originalImage,
						zoom: currentCacheLocal?.zoom,
						rotation: currentCacheLocal?.rotation,
					};
				});
			}, TIMEOUT);
		} else if (currentCache && !onCloseCallBack) {
			setOriginalImage(currentCache?.imageSource);
		}
	}, [originalImage, onCloseCallBack, queryClient]);

	const onChangeSliderZoom = (_: Event, newValue: number | number[]) => {
		const NINE = 9;
		const value = newValue as number;
		if (value > NINE) {
			const zoomVal = value / 100;
			updateZoomAmount(zoomVal);
		}
	};

	const onChangeSliderStraighten = (_: Event, newValue: number | number[]) => {
		queryClient.setQueryData([cacheIndex], (currentCache: IQueryClient | undefined) => {
			const degreeVal = (newValue as number) || 0;
			if (typeof cropper !== "undefined") {
				cropper.rotateTo(degreeVal);
			}
			return {
				imageSource: currentCache?.imageSource,
				zoom: currentCache?.zoom,
				rotation: degreeVal,
			};
		});
	};

	return (
		<>
			<Grid container id="cropper-container">
				<Grid className={`${DEFAULT_PROPS.shape} !w-[100%]`}>
					<CropperComponent
						style={{ width: `${DEFAULT_PROPS.width}px`, height: `${DEFAULT_PROPS.height}px` }}
						zoomOnWheel={DEFAULT_PROPS.zoomOnWheel}
						viewMode={DEFAULT_PROPS.viewMode as TViewMode}
						rotateTo={image?.rotation ?? 0}
						minContainerHeight={DEFAULT_PROPS.containerHeight}
						minContainerWidth={DEFAULT_PROPS.containerWidth}
						minCanvasHeight={DEFAULT_PROPS.canvasHeight}
						minCanvasWidth={DEFAULT_PROPS.canvasWidth}
						minCropBoxHeight={DEFAULT_PROPS.cropBoxHeight}
						minCropBoxWidth={DEFAULT_PROPS.cropBoxWidth}
						zoomTo={image?.zoom ?? 0.5}
						background={DEFAULT_PROPS.background}
						initialAspectRatio={DEFAULT_PROPS.initialAspectRatio}
						cropBoxMovable={DEFAULT_PROPS.cropBoxMovable}
						cropBoxResizable={DEFAULT_PROPS.cropBoxResizable}
						dragMode={DEFAULT_PROPS.dragMode as TDragMode}
						src={image?.imageSource}
						autoCropArea={DEFAULT_PROPS.autoCropArea}
						checkOrientation={DEFAULT_PROPS.checkOrientation}
						onInitialized={(instance: Cropper) => {
							setCropper(instance);
						}}
						guides={DEFAULT_PROPS.guides}
						checkCrossOrigin={DEFAULT_PROPS.checkCrossOrigin}
						aspectRatio={DEFAULT_PROPS.aspectRatio}
					/>
				</Grid>

				<Grid container className="controls-container">
					<Grid container direction={"row"} justifyContent={"space-between"}>
						<Grid style={{ width: "46%" }}>
							<Grid className={"control-img-label"} style={{ width: "100%" }}>
								Zoom
							</Grid>
							<Grid style={{ width: "100%" }}>
								<Slider
									value={image?.zoom ? Math.round(image?.zoom * 100) : 50}
									defaultValue={50}
									valueLabelDisplay="auto"
									onChange={onChangeSliderZoom}
								/>
							</Grid>
						</Grid>

						<Grid style={{ width: "46%" }}>
							<Grid className={"control-img-label"} style={{ width: "100%" }}>
								Straighten
							</Grid>
							<Grid style={{ width: "100%" }}>
								<Slider
									value={image?.rotation ?? 0}
									defaultValue={0}
									min={-90}
									max={90}
									valueLabelDisplay="auto"
									onChange={onChangeSliderStraighten}
								/>
							</Grid>
						</Grid>

						<Grid className="!w-[3%] mt-[18px]">
							<RotateRightIcon onClick={() => handleRotation(30)} className="btn-rotation" />
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			<Grid container direction={"row"} flexWrap={"nowrap"} justifyContent={"space-between"} className="cropper-actions-container">
				<UploadProfilePicture onChangeImage={handleChangeImage} />

				<Button autoFocus onClick={handleSaveImage}>
					Save changes
				</Button>
			</Grid>

			<Backdrop className="!absolute text-white" open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>
		</>
	);
}
